/**
 * fetch-Wrapper für die botagent_cloud-API. Vorbild: client.md §5, §13.
 *
 * - Jeder Aufruf schickt {token, workspace, ...} im JSON-Body (§5.1).
 * - Vor dem ersten Aufruf mit einem neuen Token wird automatisch eingeloggt (§5.2);
 *   liefert ein Aufruf `code === -1002`, wird das Token einmalig neu eingeloggt und
 *   der Aufruf wiederholt (Auto-Re-Login, §5.2/§13).
 */
import type {
  AddDocument,
  AddImage,
  AddResponse,
  AnswerResponse,
  CloudConfig,
  ConfigureResponse,
  CreateResponse,
  CritiquesResponse,
  DecisionsResponse,
  DeleteResponse,
  FilesResponse,
  GetResponse,
  LlmResponse,
  LoginResponse,
  LogsResponse,
  PlanResponse,
  PullResponse,
  ReplanResponse,
  SearchResponse,
  StartResponse,
  StatusResponse,
  VoucherResponse,
} from "./types";

export class NetworkError extends Error {}

export type ApiCtx = { apiUrl: string; token: string; workspace: string };

/** Tokens, für die in dieser Browser-Sitzung bereits erfolgreich eingeloggt wurde. */
const loggedInTokens = new Set<string>();

function normalizeApiUrl(apiUrl: string): string {
  return apiUrl.replace(/\/+$/, "");
}

export async function login(apiUrl: string, token: string): Promise<LoginResponse> {
  const url = `${normalizeApiUrl(apiUrl)}/api/chatgpt/login?token=${encodeURIComponent(token)}&mode=agent`;
  let res: Response;
  try {
    res = await fetch(url, { method: "POST" });
  } catch {
    throw new NetworkError(`Netzwerkfehler bei /api/chatgpt/login`);
  }
  if (!res.ok) throw new NetworkError(`HTTP ${res.status} bei /api/chatgpt/login`);
  try {
    return (await res.json()) as LoginResponse;
  } catch {
    throw new NetworkError("Ungültige Antwort von /api/chatgpt/login");
  }
}

const loginPromises = new Map<string, Promise<LoginResponse>>();

async function ensureLogin(apiUrl: string, token: string): Promise<LoginResponse> {
  if (loggedInTokens.has(token)) return { code: 0 };
  
  const key = `${apiUrl}:${token}`;
  if (loginPromises.has(key)) {
    return loginPromises.get(key)!;
  }

  const promise = login(apiUrl, token).then(res => {
    if (res.code === 0) loggedInTokens.add(token);
    loginPromises.delete(key);
    return res;
  }).catch(err => {
    loginPromises.delete(key);
    throw err;
  });

  loginPromises.set(key, promise);
  return promise;
}

async function postJson<T>(apiUrl: string, path: string, body: unknown): Promise<T> {
  let res: Response;
  const url = `${normalizeApiUrl(apiUrl)}${path}`;
  console.debug(`[botagent api] -> POST ${path}`, body);
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.error(`[botagent api] network error for ${path} (${url})`, e);
    throw new NetworkError(`Netzwerkfehler bei ${path}`);
  }
  if (!res.ok) {
    console.error(`[botagent api] HTTP ${res.status} for ${path}`);
    throw new NetworkError(`HTTP ${res.status} bei ${path}`);
  }
  try {
    const json = (await res.json()) as T;
    console.debug(`[botagent api] <- ${path}`, json);
    return json;
  } catch (e) {
    console.error(`[botagent api] invalid JSON response for ${path}`, e);
    throw new NetworkError(`Ungültige Antwort von ${path}`);
  }
}

async function callBotagent<T extends { code: number }>(
  ctx: ApiCtx,
  path: string,
  extra: Record<string, unknown> = {},
): Promise<T> {
  await ensureLogin(ctx.apiUrl, ctx.token);
  let res = await postJson<T>(ctx.apiUrl, path, { token: ctx.token, workspace: ctx.workspace, ...extra });
  if (res.code === -1002) {
    loggedInTokens.delete(ctx.token);
    const loginRes = await login(ctx.apiUrl, ctx.token);
    if (loginRes.code === 0) {
      loggedInTokens.add(ctx.token);
      res = await postJson<T>(ctx.apiUrl, path, { token: ctx.token, workspace: ctx.workspace, ...extra });
    }
  }
  if (res.code < 0) {
    console.error(`[botagent api] ${path} returned code ${res.code}`, (res as { msg?: string }).msg);
  }
  return res;
}

export function create(ctx: ApiCtx, task: string, config?: CloudConfig) {
  return callBotagent<CreateResponse>(ctx, "/api/botagent/create", { task, config });
}

export function start(ctx: ApiCtx, task?: string, config?: CloudConfig, max_turns?: number) {
  return callBotagent<StartResponse>(ctx, "/api/botagent/start", { task, config, max_turns });
}

export function stop(ctx: ApiCtx) {
  return callBotagent<{ code: number; msg?: string }>(ctx, "/api/botagent/stop");
}

export function plan(ctx: ApiCtx, task?: string) {
  return callBotagent<PlanResponse>(ctx, "/api/botagent/plan", { task });
}

export function replan(ctx: ApiCtx, hint?: string) {
  return callBotagent<ReplanResponse>(ctx, "/api/botagent/replan", { hint });
}

export function status(ctx: ApiCtx) {
  return callBotagent<StatusResponse>(ctx, "/api/botagent/status");
}

export function voucher(ctx: ApiCtx, value: string | number) {
  const body = typeof value === "number" ? { amount: value } : { code: value };
  return callBotagent<VoucherResponse>(ctx, "/api/botagent/voucher", body);
}

export function decisions(ctx: ApiCtx) {
  return callBotagent<DecisionsResponse>(ctx, "/api/botagent/decisions");
}

export function answer(ctx: ApiCtx, qid: string, text: string) {
  return callBotagent<AnswerResponse>(ctx, "/api/botagent/answer", { qid, text });
}

export function critiques(ctx: ApiCtx) {
  return callBotagent<CritiquesResponse>(ctx, "/api/botagent/critiques");
}

export function add(ctx: ApiCtx, documents: AddDocument[], images: AddImage[] = []) {
  return callBotagent<AddResponse>(ctx, "/api/botagent/add", { documents, images });
}

export function get(ctx: ApiCtx, doc_id: string) {
  return callBotagent<GetResponse>(ctx, "/api/botagent/get", { doc_id });
}

export function deleteDoc(ctx: ApiCtx, doc_id: string) {
  return callBotagent<DeleteResponse>(ctx, "/api/botagent/delete", { doc_id });
}

export function files(ctx: ApiCtx) {
  return callBotagent<FilesResponse>(ctx, "/api/botagent/files");
}

export function pull(ctx: ApiCtx) {
  return callBotagent<PullResponse>(ctx, "/api/botagent/pull");
}

export function search(ctx: ApiCtx, pattern: string) {
  return callBotagent<SearchResponse>(ctx, "/api/botagent/search", { pattern });
}

export function logs(ctx: ApiCtx, tail = 200) {
  return callBotagent<LogsResponse>(ctx, "/api/botagent/logs", { tail });
}

export function llm(ctx: ApiCtx) {
  return callBotagent<LlmResponse>(ctx, "/api/botagent/llm");
}

export function configure(ctx: ApiCtx, config: Partial<CloudConfig>, role?: string) {
  return callBotagent<ConfigureResponse>(ctx, "/api/botagent/configure", { config, role });
}

export function getReadme(ctx: ApiCtx) {
  return callBotagent<{ code: number; readme: string; msg?: string }>(ctx, "/api/botagent/get_readme");
}

export function saveReadme(ctx: ApiCtx, readme: string, role?: string) {
  return callBotagent<{ code: number; msg?: string }>(ctx, "/api/botagent/save_readme", { readme, role });
}

export interface ChatResponse {
  code: number;
  response: string;
  response_id: string;
  msg?: string;
}

export function chat(ctx: ApiCtx, message: string, history: { role: string; content: string }[], model?: string) {
  return callBotagent<ChatResponse>(ctx, "/api/botagent/chat", { message, history, model });
}
